import {
    CheckCircle,
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon,
    LocalShipping,
    LocationOn,
    Phone,
    Schedule,
    VerifiedUser,
} from "@mui/icons-material";
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Collapse,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    Paper,
    Rating,
    Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import PackagesApiService from "../../../../infrastructure/api/packages";
import { formatNumber } from "../../../../infrastructure/utils/formatters";

const ContractorSelection = ({
  requestId: propRequestId,
  isModal = false,
  onSelectPackage: onSelectPackageCallback,
  onClose,
}) => {
  const params = useParams();
  const requestId = propRequestId || params.requestId;
  const navigate = useNavigate();

  const baseUrl = window.globalConfig?.API_URL || "";
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [expandedPackage, setExpandedPackage] = useState(null);

  useEffect(() => {
    console.log("useEffect triggered with requestId:", requestId);
    if (requestId) {
      loadPackages();
    } else {
      console.error("requestId is null or undefined!");
      setLoading(false);
    }
  }, [requestId]);

  const loadPackages = async () => {
    try {
      setLoading(true);
      const response = await PackagesApiService.getPackagesForRequest(
        requestId
      );

      if (response.isSuccess) {
        setData(response.data);
      } else {
        toast.error(response.message || "خطا در دریافت اطلاعات");
        setData(null);
      }
    } catch (error) {
      console.error("خطا در دریافت پکیج‌ها:", error);
      toast.error("خطا در دریافت اطلاعات پکیج‌ها");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPackage = (pkg) => {
    setSelectedPackage(pkg);
    setConfirmDialogOpen(true);
  };

  const handleConfirmSelection = async () => {
    try {
      // اگر modal است و callback داریم، اطلاعات را به والد برمی‌گردانیم
      if (isModal && onSelectPackageCallback) {
        onSelectPackageCallback(selectedPackage);
        setConfirmDialogOpen(false);
        if (onClose) {
          onClose();
        }
        return;
      }

      // اگر صفحه مستقل است، API برای ثبت انتخاب پیمانکار فراخوانی می‌شود
      // TODO: باید endpoint مناسب اضافه شود
      toast.success(
        `پکیج "${selectedPackage.packageTitle}" با موفقیت انتخاب شد`
      );
      setConfirmDialogOpen(false);
      // navigate به صفحه Information
      navigate(`/SLN/Customer/Requests/Information?id=${requestId}`);
    } catch (error) {
      console.error("خطا در ثبت انتخاب:", error);
      toast.error("خطا در ثبت انتخاب پیمانکار");
    }
  };

  const toggleExpand = (packageId) => {
    setExpandedPackage(expandedPackage === packageId ? null : packageId);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!data || !data.availablePackages || data.availablePackages.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ mt: isModal ? 2 : 4 }}>
        <Alert severity="info">
          هیچ پکیجی برای این درخواست یافت نشد. لطفاً بعداً دوباره تلاش کنید.
        </Alert>
        {!isModal && (
          <Box mt={2}>
            <Button variant="contained" onClick={() => navigate(-1)}>
              بازگشت
            </Button>
          </Box>
        )}
      </Container>
    );
  }

  return (
    <Container
      maxWidth="xl"
      sx={{ mt: isModal ? 2 : 4, mb: isModal ? 2 : 4, px: isModal ? 2 : 3 }}
    >
      {/* Header */}
      <Box mb={3}>
        {!isModal && (
          <Typography variant="h4" gutterBottom>
            انتخاب پیمانکار و پکیج
          </Typography>
        )}
        <Typography variant="body1" color="text.secondary">
          ظرفیت درخواستی: <strong>{data.requestCapacity} کیلووات</strong> |
          تعداد پکیج‌های موجود: <strong>{data.totalCount}</strong>
        </Typography>
        {data.suggestedContractorCompanyName ? (
          <Alert severity="success" sx={{ mt: 2 }}>
            پیمانکار پیشنهادی سولارنت (از لینک بازاریابی):{" "}
            <strong>{data.suggestedContractorCompanyName}</strong>
          </Alert>
        ) : null}
      </Box>

      {/* Packages Grid */}
      <Grid container spacing={3}>
        {data.availablePackages.map((pkg) => (
          <Grid item xs={12} key={pkg.packageId}>
            <Card
              elevation={3}
              sx={{
                borderRight: 4,
                borderColor: pkg.isRecommendedByReferral ? "success.main" : "primary.main",
                transition: "all 0.3s",
                "&:hover": {
                  boxShadow: 6,
                  transform: "translateY(-2px)",
                },
              }}
            >
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  {/* عنوان و اطلاعات اصلی */}
                  <Grid item xs={12} md={4}>
                    <Typography variant="h6" gutterBottom>
                      {pkg.packageTitle}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Chip
                        label={`${pkg.capacity} کیلووات`}
                        color="primary"
                        size="small"
                      />
                      {pkg.isRecommendedByReferral ? (
                        <Chip label="پیشنهاد سولارنت" color="success" size="small" />
                      ) : null}
                      {pkg.status === 1 && (
                        <Chip
                          icon={<CheckCircle />}
                          label="تایید شده"
                          color="success"
                          size="small"
                        />
                      )}
                    </Box>

                    {/* اطلاعات پیمانکار */}
                    <Box mt={2}>
                      <Typography variant="subtitle2" color="text.secondary">
                        پیمانکار
                      </Typography>
                      <Box display="flex" alignItems="center" gap={2} mt={1}>
                        <Avatar
                          sx={{
                            width: 56,
                            height: 56,
                            border: "2px solid",
                            borderColor: "divider",
                            "& img": {
                              objectFit: "cover",
                            },
                          }}
                          src={
                            pkg.contractorRegistrationType === "legal"
                              ? pkg.contractorCompanyLogoUrl
                                ? `${baseUrl}${pkg.contractorCompanyLogoUrl}`
                                : undefined
                              : pkg.contractorProfileImageUrl
                              ? `${baseUrl}${pkg.contractorProfileImageUrl}`
                              : undefined
                          }
                        />
                        <Box flex={1}>
                          <Typography variant="body1" fontWeight="bold">
                            {pkg.contractorName}
                          </Typography>
                          {pkg.contractorCompany && (
                            <Typography variant="body2" color="text.secondary">
                              {pkg.contractorCompany}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1} mt={1}>
                        <LocationOn fontSize="small" color="action" />
                        <Typography variant="body2">
                          {pkg.contractorCity}
                          {pkg.contractorProvince &&
                            `, ${pkg.contractorProvince}`}
                        </Typography>
                      </Box>
                      {pkg.contractorPhone && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Phone fontSize="small" color="action" />
                          <Typography variant="body2" dir="ltr">
                            {pkg.contractorPhone}
                          </Typography>
                        </Box>
                      )}
                      {pkg.contractorRating && (
                        <Box display="flex" alignItems="center" gap={1} mt={1}>
                          <Rating
                            value={pkg.contractorRating}
                            readOnly
                            size="small"
                            precision={0.5}
                          />
                          <Typography variant="body2">
                            ({pkg.contractorRating.toFixed(1)})
                          </Typography>
                        </Box>
                      )}
                      {pkg.contractorWorkExperience && (
                        <Typography variant="body2" color="text.secondary">
                          سابقه: {pkg.contractorWorkExperience}
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  {/* قیمت‌ها */}
                  <Grid item xs={12} md={4}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, bgcolor: "background.default" }}
                    >
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        قیمت‌ها (ریال)
                      </Typography>
                      {pkg.priceWithEquipment && (
                        <Box mb={1}>
                          <Typography variant="body2" color="text.secondary">
                            با تجهیزات:
                          </Typography>
                          <Typography variant="h6" color="primary.main">
                            {formatNumber(pkg.priceWithEquipment)}
                          </Typography>
                        </Box>
                      )}
                      {pkg.priceWithoutEquipment && (
                        <Box mb={1}>
                          <Typography variant="body2" color="text.secondary">
                            بدون تجهیزات:
                          </Typography>
                          <Typography variant="h6">
                            {formatNumber(pkg.priceWithoutEquipment)}
                          </Typography>
                        </Box>
                      )}
                      {pkg.deliveryFee > 0 && !pkg.contractorPaysDelivery && (
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <LocalShipping fontSize="small" />
                            <Typography variant="body2">
                              هزینه ارسال:
                            </Typography>
                          </Box>
                          <Typography variant="body2" fontWeight="bold">
                            {formatNumber(pkg.deliveryFee)}
                          </Typography>
                        </Box>
                      )}
                      {pkg.contractorPaysDelivery && (
                        <Chip
                          icon={<LocalShipping />}
                          label="ارسال رایگان"
                          color="success"
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      )}
                      <Divider sx={{ my: 1.5 }} />
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Typography variant="body2" fontWeight="bold">
                          قیمت نهایی:
                        </Typography>
                        <Typography
                          variant="h5"
                          color="success.main"
                          fontWeight="bold"
                        >
                          {formatNumber(pkg.totalPriceWithEquipment)}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>

                  {/* شرایط و اقدامات */}
                  <Grid item xs={12} md={4}>
                    <Box>
                      {pkg.installationDurationDays && (
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Schedule color="action" />
                          <Typography variant="body2">
                            مدت اجرا: {pkg.installationDurationDays} روز
                          </Typography>
                        </Box>
                      )}
                      {pkg.warrantyMonths && (
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <VerifiedUser color="action" />
                          <Typography variant="body2">
                            گارانتی: {pkg.warrantyMonths} ماه
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <Box mt={2}>
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        size="large"
                        onClick={() => handleSelectPackage(pkg)}
                        sx={{ mb: 1 }}
                      >
                        انتخاب این پکیج
                      </Button>
                      <Button
                        variant="outlined"
                        fullWidth
                        size="small"
                        startIcon={
                          expandedPackage === pkg.packageId ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )
                        }
                        onClick={() => toggleExpand(pkg.packageId)}
                      >
                        {expandedPackage === pkg.packageId
                          ? "بستن جزئیات"
                          : "مشاهده جزئیات"}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>

                {/* جزئیات بیشتر */}
                <Collapse
                  in={expandedPackage === pkg.packageId}
                  timeout="auto"
                  unmountOnExit
                >
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={2}>
                    {pkg.description && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>
                          توضیحات:
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {pkg.description}
                        </Typography>
                      </Grid>
                    )}
                    {pkg.features && pkg.features.length > 0 && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          ویژگی‌ها:
                        </Typography>
                        <ul style={{ margin: 0, paddingRight: 20 }}>
                          {pkg.features.map((feature, index) => (
                            <li key={index}>
                              <Typography variant="body2">{feature}</Typography>
                            </li>
                          ))}
                        </ul>
                      </Grid>
                    )}
                    {pkg.equipments && pkg.equipments.length > 0 && (
                      <Grid item xs={12}>
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          sx={{ fontWeight: "bold", mb: 2 }}
                        >
                          تجهیزات پکیج ({pkg.equipments.length} قلم):
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          {pkg.equipments.map((equipment, index) => {
                            // اگر equipment یک string ساده است، آن را parse می‌کنیم
                            const isString = typeof equipment === "string";
                            let displayData = {};

                            if (isString) {
                              // Parse کردن string مثل "1x گروات 12033 (Inverter)"
                              const match = equipment.match(
                                /^(\d+)x?\s*(.+?)\s*\((.+?)\)$/
                              );
                              if (match) {
                                displayData = {
                                  quantity: match[1],
                                  brand: match[2].trim(),
                                  equipmentType: match[3].trim(),
                                  name: equipment,
                                };
                              } else {
                                displayData = { name: equipment };
                              }
                            } else {
                              displayData = equipment;
                            }

                            return (
                              <Paper
                                key={index}
                                variant="outlined"
                                sx={{
                                  p: 2,
                                  mb: 2,
                                  borderLeft: 4,
                                  borderLeftColor: "primary.main",
                                  "&:hover": {
                                    boxShadow: 2,
                                    borderLeftColor: "primary.dark",
                                  },
                                }}
                              >
                                <Grid container spacing={2}>
                                  {/* ردیف و نوع تجهیز */}
                                  <Grid item xs={12}>
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      gap={1}
                                      flexWrap="wrap"
                                    >
                                      <Chip
                                        label={`#${index + 1}`}
                                        size="small"
                                        color="primary"
                                      />
                                      <Typography
                                        variant="h6"
                                        fontWeight="bold"
                                      >
                                        {displayData.equipmentType ||
                                          displayData.name ||
                                          "تجهیز"}
                                      </Typography>
                                      {displayData.quantity && (
                                        <Chip
                                          label={`تعداد: ${displayData.quantity}`}
                                          size="small"
                                          color="success"
                                          variant="outlined"
                                        />
                                      )}
                                    </Box>
                                  </Grid>

                                  {!isString && (
                                    <>
                                      {/* برند و مدل */}
                                      {(displayData.brand ||
                                        displayData.model) && (
                                        <Grid item xs={12} sm={6}>
                                          <Paper
                                            sx={{ p: 1.5, bgcolor: "grey.50" }}
                                          >
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              display="block"
                                            >
                                              برند و مدل
                                            </Typography>
                                            <Typography
                                              variant="body1"
                                              fontWeight="medium"
                                            >
                                              {displayData.brand || "نامشخص"}
                                              {displayData.model &&
                                                ` - ${displayData.model}`}
                                            </Typography>
                                          </Paper>
                                        </Grid>
                                      )}

                                      {/* تعداد */}
                                      {displayData.quantity && (
                                        <Grid item xs={6} sm={3}>
                                          <Paper
                                            sx={{ p: 1.5, bgcolor: "grey.50" }}
                                          >
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              display="block"
                                            >
                                              تعداد
                                            </Typography>
                                            <Typography
                                              variant="h6"
                                              color="primary"
                                            >
                                              {displayData.quantity}
                                            </Typography>
                                          </Paper>
                                        </Grid>
                                      )}

                                      {/* گارانتی */}
                                      {displayData.warrantyPeriod && (
                                        <Grid item xs={6} sm={3}>
                                          <Paper
                                            sx={{ p: 1.5, bgcolor: "grey.50" }}
                                          >
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              display="block"
                                            >
                                              گارانتی
                                            </Typography>
                                            <Typography
                                              variant="body1"
                                              fontWeight="medium"
                                            >
                                              {displayData.warrantyPeriod} ماه
                                            </Typography>
                                          </Paper>
                                        </Grid>
                                      )}

                                      {/* قیمت واحد */}
                                      {displayData.unitPrice && (
                                        <Grid item xs={6} sm={4}>
                                          <Paper
                                            sx={{
                                              p: 1.5,
                                              bgcolor: "success.50",
                                            }}
                                          >
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              display="block"
                                            >
                                              قیمت واحد
                                            </Typography>
                                            <Typography
                                              variant="body1"
                                              fontWeight="bold"
                                              color="success.main"
                                            >
                                              {formatNumber(
                                                displayData.unitPrice
                                              )}{" "}
                                              ریال
                                            </Typography>
                                          </Paper>
                                        </Grid>
                                      )}

                                      {/* قیمت کل */}
                                      {displayData.totalPrice && (
                                        <Grid item xs={6} sm={4}>
                                          <Paper
                                            sx={{
                                              p: 1.5,
                                              bgcolor: "success.100",
                                            }}
                                          >
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              display="block"
                                            >
                                              قیمت کل
                                            </Typography>
                                            <Typography
                                              variant="h6"
                                              fontWeight="bold"
                                              color="success.dark"
                                            >
                                              {formatNumber(
                                                displayData.totalPrice
                                              )}{" "}
                                              ریال
                                            </Typography>
                                          </Paper>
                                        </Grid>
                                      )}

                                      {/* توضیحات */}
                                      {displayData.description && (
                                        <Grid item xs={12}>
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                            gutterBottom
                                          >
                                            توضیحات:
                                          </Typography>
                                          <Typography variant="body2">
                                            {displayData.description}
                                          </Typography>
                                        </Grid>
                                      )}

                                      {/* مشخصات فنی */}
                                      {displayData.specifications && (
                                        <Grid item xs={12}>
                                          <Paper
                                            sx={{ p: 1.5, bgcolor: "info.50" }}
                                          >
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              display="block"
                                              gutterBottom
                                            >
                                              مشخصات فنی:
                                            </Typography>
                                            <Typography variant="body2">
                                              {displayData.specifications}
                                            </Typography>
                                          </Paper>
                                        </Grid>
                                      )}
                                    </>
                                  )}

                                  {/* اگر string ساده است، برند را نمایش بده */}
                                  {isString && displayData.brand && (
                                    <Grid item xs={12}>
                                      <Paper
                                        sx={{ p: 1.5, bgcolor: "grey.50" }}
                                      >
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          display="block"
                                        >
                                          مشخصات
                                        </Typography>
                                        <Typography
                                          variant="body1"
                                          fontWeight="medium"
                                        >
                                          {displayData.brand}
                                        </Typography>
                                      </Paper>
                                    </Grid>
                                  )}
                                </Grid>
                              </Paper>
                            );
                          })}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Collapse>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Confirm Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>تایید انتخاب پیمانکار</DialogTitle>
        <DialogContent>
          {selectedPackage && (
            <Box>
              <Typography variant="body1" gutterBottom>
                آیا از انتخاب این پکیج مطمئن هستید؟
              </Typography>
              <Box mt={2} p={2} bgcolor="grey.100" borderRadius={1}>
                <Typography variant="subtitle2" gutterBottom>
                  <strong>پکیج:</strong> {selectedPackage.packageTitle}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>پیمانکار:</strong> {selectedPackage.contractorName}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>قیمت نهایی:</strong>{" "}
                  {formatNumber(selectedPackage.totalPriceWithEquipment)} ریال
                </Typography>
                {selectedPackage.installationDurationDays && (
                  <Typography variant="body2" gutterBottom>
                    <strong>مدت اجرا:</strong>{" "}
                    {selectedPackage.installationDurationDays} روز
                  </Typography>
                )}
              </Box>
              <Alert severity="info" sx={{ mt: 2 }}>
                پس از تایید، قرارداد با این پیمانکار منعقد خواهد شد.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>انصراف</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmSelection}
          >
            تایید و ادامه
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ContractorSelection;
